import {
  getProducts,
  getReceiptSystemAccountIdSafe,
  type Products,
} from '@forest/native-services'
import { tagFailure, type PageLoadStep } from '@forest/paywall-core'
import { getChinaAndroidTrialEligibility } from '~/lib/get-china-android-trial-eligibility'
import { appVersion, platform } from '~/lib/params'
import { api } from '~/services/api'
import { userInfoSource } from '~/store/user-info'

export type ChinaAndroidTrialEligibility = 'available' | 'unavailable'

export type Platform = 'ios' | 'googleplay_android' | 'china_android'

export interface PaywallBootstrap {
  products: Products
  receiptSystemAccountId: string | null
  chinaAndroidTrialEligibility: ChinaAndroidTrialEligibility
}

export interface PaywallBootstrapDeps {
  platform: Platform
  fetchProducts: () => Promise<Products>
  fetchReceiptSystemAccountId: () => Promise<string | null>
  fetchChinaAndroidTrialEligibility: () => Promise<ChinaAndroidTrialEligibility>
}

export async function loadPaywallBootstrap(
  deps?: PaywallBootstrapDeps,
): Promise<PaywallBootstrap> {
  const resolved = deps ?? (await loadProductionDeps())
  const trialPromise: Promise<ChinaAndroidTrialEligibility> =
    resolved.platform === 'china_android'
      ? inStep(
          'load_trial_eligibility',
          resolved.fetchChinaAndroidTrialEligibility(),
        )
      : Promise.resolve('unavailable')
  const [products, receiptSystemAccountId, chinaAndroidTrialEligibility] =
    await Promise.all([
      inStep('load_products', resolved.fetchProducts()),
      resolved.fetchReceiptSystemAccountId(),
      trialPromise,
    ])
  return { products, receiptSystemAccountId, chinaAndroidTrialEligibility }
}

/** Tags what a page-load step throws with the step (ADR 0037). */
function inStep<T>(step: PageLoadStep, work: Promise<T>): Promise<T> {
  return work.catch((error: unknown) => {
    throw tagFailure(error, step)
  })
}

export function selectIsTrialEligible(bootstrap: PaywallBootstrap): boolean {
  if (bootstrap.chinaAndroidTrialEligibility === 'available') return true
  return (bootstrap.products.annualEarlyBird.eligible_trial_days ?? 0) > 0
}

async function loadProductionDeps(): Promise<PaywallBootstrapDeps> {
  return {
    platform,
    fetchProducts: () =>
      getProducts({
        cacheKeyPrefix: 'birthdayCampaign.products.v1',
        api,
        appVersion: appVersion || '0.0.0',
        platform,
        deferredUserInfo: userInfoSource.getReady(),
        env: import.meta.env.MODE as 'production' | 'staging' | 'dev',
        shouldMock: import.meta.env.DEV,
      }),
    fetchReceiptSystemAccountId: () =>
      getReceiptSystemAccountIdSafe(appVersion || '0.0.0'),
    fetchChinaAndroidTrialEligibility: async () => {
      const userInfo = await userInfoSource.getReady()
      return getChinaAndroidTrialEligibility(userInfo.user_id)
    },
  }
}
